import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, memo } from "react"

import { AnimatePresence } from "framer-motion"
import { Helmet } from "react-helmet-async"
import { useParams } from "react-router-dom"
import { useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import { ResetLocationModal } from "~/components/GeocodeMap/ResetLocationModal"
import {
  RetiredImageryUnavailable,
  inertWhen,
} from "~/components/RetiredImagery/RetiredImageryUnavailable"
import { isEditingPaused } from "~/components/RetiredImagery/useRetiredImageryStatus"
import { ModalUnderlay } from "~/containers/proposal-styles"
import { useRequiredPermissions } from "~/hooks/useRequiredPermissions"

import { DESIGNER_ALLOWED_PERMISSIONS } from "./constants"
import type {
  ContinuePhase,
  DesignerCapabilities,
} from "./designer-capabilities"
import {
  DesignerCapabilitiesProvider,
  V1_DESIGNER_FEATURES,
  useDesignerCapabilities,
} from "./designer-capabilities"
import { DesignSwitcher, showDesignSwitcherState } from "./DesignSwitcher"
import { Drawer } from "./Drawer"
import { Keybinds } from "./Keybinds"
import { LeftPanel, RightPanel } from "./SidePanel"
import { ConfirmPhaseTransitionModal } from "./SidePanel/ConfirmPhaseTransitionModal"
import { ImageSelection } from "./SidePanel/ImageSelection"
import { FloatingSaveButton } from "./SidePanel/SaveDesignButtonFloating"
import { useResetZoomAndPan } from "./state/elements/transactions/resetZoomAndPan"
import { phaseState } from "./state/phase"
import { resetLocationModalIsOpenState } from "./state/view"
import { Studio } from "./Studio"
import { DebugPortal } from "./Studio/Debug/DebugPortal"
import { TopBar } from "./TopBar"
import { useLegacyRetiredImageryStatus } from "./useLegacyRetiredImageryStatus"
import { useBrochure } from "../Proposal/state/brochure/use-brochure"
import { useClaimDesignRequestUntil } from "../Proposal/state/design-request/use-design-request"
import { useDesignContext } from "../Proposal/state/designs/design-context"
import {
  useCompleteDesign,
  useFetchDesignState,
  useSyncDesign,
} from "../Proposal/state/designs/use-design"

const Layer = styled.span`
  width: 100vw;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  overflow: hidden;
  position: relative;
  display: flex;
  flex-flow: column;
  isolation: isolate;
`

const MainStrip = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: row;
  overflow-y: auto;
`

/**
 * The Legacy Designer UI. Renders only: it runs no design fetch, brochure or
 * Design Request hooks. The host supplies a hydrated RecoilRoot, design
 * actions (`DesignActionsProvider` or the V1 `DesignContextProvider`) and
 * `DesignerCapabilitiesProvider`.
 */
export const DesignerShell: FC<{ onUserActivity?: VoidFunction }> = memo(
  ({ onUserActivity }) => {
    useSyncDesign()
    const design = useDesignContext()
    const { designSwitcher, resetLocation } = useDesignerCapabilities()
    const resetZoomAndPan = useResetZoomAndPan()
    const [locationResetModalIsOpen, setLocationResetModalIsOpen] =
      useRecoilState(resetLocationModalIsOpenState)
    useEffect(() => {
      resetZoomAndPan()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const showDesignSwitcher =
      useRecoilValue(showDesignSwitcherState) && designSwitcher

    const designPhase = useRecoilValue(phaseState)
    const retiredImagery = useLegacyRetiredImageryStatus()
    const isImageUnavailable = retiredImagery.status === `unavailable`
    const isEditingBlocked = isEditingPaused(retiredImagery.status)

    const title = `Designer | ${designPhase}`

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {!isEditingBlocked && <Keybinds design={design} />}
        <Layer onMouseMove={onUserActivity}>
          {resetLocation && (
            <>
              <ModalUnderlay
                isInUse={locationResetModalIsOpen}
                onClick={() => setLocationResetModalIsOpen(false)}
              />
              {locationResetModalIsOpen && <ResetLocationModal />}
            </>
          )}
          <ConfirmPhaseTransitionModal />
          {/* `contents` keeps the flex layout; `inert` blocks editing behind the notice */}
          <div className="contents" {...inertWhen(isEditingBlocked)}>
            <Drawer />
            <TopBar />
            <MainStrip>
              <AnimatePresence mode="wait">
                {showDesignSwitcher && (
                  <DesignSwitcher key={`design-switcher-animated`} />
                )}
                {!showDesignSwitcher && (
                  <LeftPanel
                    key={`left-panel-studio-animated`}
                    design={design}
                  />
                )}
              </AnimatePresence>
              <FloatingSaveButton design={design} />

              <Studio />

              <RightPanel save={design.save} />
            </MainStrip>
          </div>
          {isImageUnavailable && (
            <RetiredImageryUnavailable
              onRetry={retiredImagery.retry}
              isReplacement={retiredImagery.isReplacement}
            >
              <ImageSelection isRecovery />
            </RetiredImageryUnavailable>
          )}
        </Layer>
        <DebugPortal />
      </>
    )
  },
)

DesignerShell.displayName = `DesignerShell`

/** The V1 designer: runs the V1 data hooks, then renders `DesignerShell`. */
export const Designer: FC<{ designRequestId: string }> = memo(
  ({ designRequestId }) => {
    useRequiredPermissions([...DESIGNER_ALLOWED_PERMISSIONS], `../`)
    useFetchDesignState({ designRequestId })
    const design = useDesignContext()
    useBrochure(design?.local?.proposalId)
    const { lastActive } = useClaimDesignRequestUntil({ designRequestId })
    const completeDesign = useCompleteDesign({ design })
    // A ref keeps `continuePhase` stable so capability consumers do not
    // re-render on every local design change.
    const completeDesignRef = useRef(completeDesign)
    useEffect(() => {
      completeDesignRef.current = completeDesign
    }, [completeDesign])
    const continuePhase = useCallback<ContinuePhase>(
      (options) => completeDesignRef.current(options),
      [],
    )
    const capabilities = useMemo(
      (): DesignerCapabilities => ({
        ...V1_DESIGNER_FEATURES,
        backTarget: { kind: `v1-proposal` },
        continuePhase,
        systemInfo: { kind: `brochure` },
      }),
      [continuePhase],
    )

    const handleUserActivity = useCallback(() => {
      lastActive.current = Date.now()
    }, [lastActive])

    return (
      <DesignerCapabilitiesProvider capabilities={capabilities}>
        <DesignerShell onUserActivity={handleUserActivity} />
      </DesignerCapabilitiesProvider>
    )
  },
)

Designer.displayName = `Designer`

export {
  DesignerCapabilitiesProvider,
  SHELL_ONLY_DESIGNER_FEATURES,
  V1_DESIGNER_FEATURES,
} from "./designer-capabilities"
export type {
  ContinuePhase,
  DesignerBackTarget,
  DesignerCapabilities,
  DesignerFeatureFlags,
  DesignerSystemInfoSource,
} from "./designer-capabilities"

const ParamIsolator: FC = () => {
  const { designRequestId } = useParams<{ designRequestId: string }>()

  return <Designer designRequestId={designRequestId ?? ``} />
}

export default ParamIsolator
